import { useMemo, useCallback } from 'react'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import { isPersonalWorkspace } from '../utils/workspaceKind'
import { requestWorkspaceSwitch } from '../services/workspaceSwitchService'
import { safeWorkspaceLanding, safeMemberLanding } from '../services/workspaceSwitch/switchLanding'

export interface UseLaunchPersonalWorkspaceResult {
  readonly hasPersonal: boolean
  readonly activateExisting: () => boolean
}

/**
 * Locates the current user's personal workspace and provides an
 * `activateExisting` handler that switches the workspace context to
 * it. Returns `hasPersonal: false` when the user has no personal
 * workspace — callers should render a "Create Personal Space" CTA
 * that navigates to the desktop `/personal` route (where OnboardingGate
 * mounts its personal-setup flow).
 *
 * This hook is intentionally minimal — it does NOT open OnboardingGate's
 * inline setup sheet. That extraction is a separate follow-up
 * (see docs/plans/personal-workspace-in-member-app.md Phase 8 NON-GOALS).
 *
 * See docs/plans/personal-workspace-in-member-app.md §2 Phase 8b.
 */
export function useLaunchPersonalWorkspace(): UseLaunchPersonalWorkspaceResult {
  const { currentUser } = useAuth()
  const { workspaces, setAppMode } = useWorkspace()

  const currentUid = currentUser?.uid
  const personalWorkspace = useMemo(() => {
    if (!currentUid) return null
    return workspaces.find(
      (workspace) => isPersonalWorkspace(workspace) && workspace.ownerId === currentUid,
    ) ?? null
  }, [currentUid, workspaces])

  const activateExisting = useCallback(() => {
    if (!personalWorkspace) return false
    setAppMode('PERSONAL')
    const landing = typeof window !== 'undefined' && window.location.pathname.startsWith('/member')
      ? safeMemberLanding({ workspace: personalWorkspace, tab: 'for-you' })
      : safeWorkspaceLanding(personalWorkspace, 'DASHBOARD')
    void requestWorkspaceSwitch({
      toWorkspaceId: personalWorkspace.id,
      source: 'USER',
      landing,
    })
    return true
  }, [personalWorkspace, setAppMode])

  return {
    hasPersonal: Boolean(personalWorkspace),
    activateExisting,
  }
}
